import { useCallback } from "react";

import DocumentationIconButton from "components/DocumentationIconButton";
import { ArrowLeft } from "components/icons/generated";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import useTypedContext from "hooks/useTypedContext";
import { TrackAnalyticsEventProperties } from "shared/Analytics";
import { getDocsUrl } from "utils/getDocsUrl";

import { CONTEXT_ANALYTICS_VERSION } from "../constants";
import { ContextFormContext } from "../context";
import NewContextFooterContinueButton from "./ContinueButton";
import NewContextFooterCreateContextButton from "./CreateContextButton";
import styles from "./styles.module.css";

type NewContextFooterProps = {
  onCreateContextClick: () => void;
  isCreatingContext: boolean;
  onContinueClick?: () => void;
  onBackClick?: () => void;
  continueClickAnalyticsProps?: TrackAnalyticsEventProperties;
  runFormValidations?: () => Promise<boolean>;
};

const NewContextFooter = ({
  onCreateContextClick,
  isCreatingContext,
  onContinueClick,
  onBackClick,
  continueClickAnalyticsProps,
  runFormValidations = async () => true,
}: NewContextFooterProps) => {
  const { currentStepData, goToNextStep, isFirstStep, isLastStep, goToPreviousStep } =
    useTypedContext(ContextFormContext);

  const handleOnContinueClick = useCallback(async () => {
    const isValidForm = await runFormValidations();
    if (!isValidForm) return;

    if (onContinueClick) {
      onContinueClick();
      return;
    }
    goToNextStep();
  }, [goToNextStep, onContinueClick, runFormValidations]);

  const handleOnBackClick = useCallback(() => {
    if (onBackClick) {
      onBackClick();
      return;
    }
    goToPreviousStep();
  }, [goToPreviousStep, onBackClick]);

  const createContextButton = (
    <NewContextFooterCreateContextButton
      isConfirmationActive={!isLastStep}
      variant={isLastStep ? "primary" : "secondary"}
      onClickHandler={onCreateContextClick}
      loading={isCreatingContext}
    >
      Create context
    </NewContextFooterCreateContextButton>
  );

  return (
    <FullScreenModalFooter justify="between">
      <DocumentationIconButton
        href={getDocsUrl("/concepts/configuration/context")}
        tooltipText="Go to contexts documentation"
      />
      <Box direction="row" gap="large">
        {!isLastStep && createContextButton}

        {!isFirstStep && (
          <Button
            variant="secondary"
            onPress={handleOnBackClick}
            className={styles.buttonWithIcon}
            disabled={isCreatingContext}
            startIcon={ArrowLeft}
          >
            Back
          </Button>
        )}

        {!isLastStep && (
          <NewContextFooterContinueButton
            analyticsTitle="Wizard - Continue to Next Step Clicked"
            analyticsProps={{
              version: CONTEXT_ANALYTICS_VERSION,
              location: currentStepData?.shortName,
              ...continueClickAnalyticsProps,
            }}
            variant="primary"
            onClickHandler={handleOnContinueClick}
            disabled={isCreatingContext}
          />
        )}

        {isLastStep && createContextButton}
      </Box>
    </FullScreenModalFooter>
  );
};

export default NewContextFooter;
